import { useCallback, useEffect, useState } from 'react';
import type { MapLabel } from '@common/schemas/entities/location.schema';

const generateUid = () => Math.random().toString(36).substring(2, 9);

export function useMapLabels(initialLabels?: MapLabel[] | null) {
    const [mapLabels, setMapLabels] = useState<MapLabel[]>([]);
    const [selectedLabelUid, setSelectedLabelUid] = useState<string | null>(null);

    // Sync from DB
    useEffect(() => {
        setMapLabels(initialLabels ?? []);
    }, [initialLabels]);

    const addLabel = useCallback((x: number, y: number, text: string = "New Label") => {
        const uid = generateUid();
        const newLabel: MapLabel = {
            uid,
            text,
            x,
            y,
            fontSize: 32,
            color: '#ffffff',
            fontFamily: 'Cinzel',
            fontWeight: 'bold',
            fontStyle: 'normal',
            outlineColor: '#000000',
            outlineWidth: 3,
            rotation: 0
        };
        setMapLabels(prev => [...prev, newLabel]);
        setSelectedLabelUid(uid);
    }, []);

    const updateLabel = useCallback((uid: string, updates: Partial<Omit<MapLabel, 'uid'>>) => {
        setMapLabels(prev => prev.map(label => label.uid === uid ? { ...label, ...updates } : label));
    }, []);

    const deleteLabel = useCallback((uid: string) => {
        setMapLabels(prev => prev.filter(label => label.uid !== uid));
        setSelectedLabelUid(prev => (prev === uid ? null : prev));
    }, []);

    return {
        mapLabels,
        setMapLabels,
        selectedLabelUid,
        setSelectedLabelUid,
        addLabel,
        updateLabel,
        deleteLabel
    };
}
